import React, { useEffect } from 'react';
import { motion, useMotionValue, useSpring } from 'framer-motion';
import Dither from './Dither';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../data/translations';

export default function Hero() {
    const { language } = useLanguage();
    const t = translations[language];
    const cursorX = useMotionValue(-100);
    const cursorY = useMotionValue(-100);

    // Spring physics configuration for the custom cursor
    const springConfig = { damping: 25, stiffness: 120, mass: 0.5 };
    const cursorXSpring = useSpring(cursorX, springConfig);
    const cursorYSpring = useSpring(cursorY, springConfig);

    useEffect(() => {
        const handleMouseMove = (e) => {
            cursorX.set(e.clientX - 16); // Center the 32px cursor ring
            cursorY.set(e.clientY - 16);
        };

        window.addEventListener('mousemove', handleMouseMove);
        return () => window.removeEventListener('mousemove', handleMouseMove);
    }, [cursorX, cursorY]);

    const wordVariants = {
        hidden: { y: "110%", opacity: 0 },
        visible: { 
            y: 0, opacity: 1, 
            transition: { duration: 1.5, ease: [0.16, 1, 0.3, 1] } 
        }
    };

    return (
        <section className="w-full h-screen relative overflow-hidden group bg-[#09090b] flex flex-col items-center justify-center cursor-none z-0">
            
            {/* Custom Framer Motion Spring Cursor */}
            <motion.div
                className="pointer-events-none fixed top-0 left-0 z-[9999] w-8 h-8 rounded-full border border-[#38bdf8] mix-blend-screen"
                style={{
                    x: cursorXSpring,
                    y: cursorYSpring,
                }}
            />

            {/* Local Dither Wave Background */}
            <div className="absolute inset-0 z-0 pointer-events-auto opacity-70 border-b border-border/20">
                <Dither
                    waveColor={[56/255, 189/255, 248/255]} 
                    disableAnimation={false}
                    enableMouseInteraction
                    mouseRadius={0.3}
                    colorNum={4}
                    waveAmplitude={0.3}
                    waveFrequency={3}
                    waveSpeed={0.05}
                />
            </div>

            {/* Central Massive Typography */}
            <div className="relative z-10 flex flex-col items-center justify-center pointer-events-none px-4 mix-blend-difference select-none">
                <div className="overflow-hidden flex gap-4 md:gap-8">
                    <motion.h1 
                        initial="hidden" animate="visible" variants={wordVariants}
                        className="font-heading text-[15vw] md:text-[12vw] leading-[0.8] tracking-[-0.02em] text-[#fafafa] uppercase"
                    >
                        MORA
                    </motion.h1>
                    <motion.h1 
                        initial="hidden" animate="visible" variants={wordVariants} transition={{ delay: 0.1 }}
                        className="font-heading text-[15vw] md:text-[12vw] leading-[0.8] tracking-[-0.02em] text-[#fafafa] uppercase"
                    >
                        TADEO.
                    </motion.h1>
                </div>
            </div>

            {/* Top Glass Letterbox (PINNED TOP) */}
            <div className="absolute top-0 left-0 w-full bg-[#09090b]/40 backdrop-blur-xl border-b border-white/10 z-[50] pointer-events-none pt-24 pb-4 px-6 md:px-12 md:pt-28">
                <div className="w-full grid-sys relative">
                    <div className="col-span-12 flex justify-between items-end mt-2">
                        <motion.div 
                            initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 1, duration: 0.8 }}
                            className="flex flex-col relative pointer-events-auto cursor-none group/title"
                        >
                            <h2 className="text-xs md:text-sm font-mono tracking-[0.2em] text-white/40 group-hover/title:text-[#38bdf8] transition-colors duration-500">
                                {t.hero_studio_overview}
                            </h2>
                        </motion.div>
                
                        {/* Fixed Agency Identity Pill - Glassmorphic */}
                        <motion.button 
                            onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                            initial={{ opacity: 0, scale: 0.9, filter: "blur(4px)" }} animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }} transition={{ delay: 1.2, duration: 0.5 }}
                            className="hidden md:flex items-center gap-4 rounded-full border border-white/5 px-6 py-2 bg-white/5 backdrop-blur-md hover:bg-white/10 hover:border-[#38bdf8]/50 hover:shadow-[0_0_20px_rgba(56,189,248,0.2)] transition-all duration-500 cursor-none pointer-events-auto z-50 group/pill"
                        >
                            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/50 group-hover/pill:text-[#38bdf8] transition-colors duration-500">{t.hero_digital_studio}</span>
                            <span className="font-mono text-[10px] uppercase tracking-[0.2em] font-bold text-[#fafafa]">MORA TADEO</span>
                        </motion.button>
                    </div>
                </div>
            </div>

            {/* Bottom Glass Letterbox Grid (PINNED BOTTOM) */}
            <div className="absolute bottom-0 left-0 w-full bg-[#09090b]/40 backdrop-blur-xl border-t border-white/10 z-[50] pointer-events-none pt-4 pb-4 lg:pb-12 px-6 md:px-12">
                <motion.div 
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1.5, duration: 1 }}
                    className="w-full grid grid-cols-2 md:grid-cols-4 gap-y-4 gap-x-6 md:gap-8 items-center"
                >
                    <div className="col-span-1 flex flex-col gap-1 md:gap-2 group/stat cursor-none pointer-events-auto">
                        <span className="font-mono text-[8px] md:text-[9px] text-white/40 uppercase tracking-[0.2em] group-hover/stat:text-[#38bdf8] transition-colors duration-500">{t.hero_established}</span>
                        <span className="font-mono text-[10px] md:text-xs uppercase text-[#fafafa] tracking-wider">2024</span>
                    </div>
                    
                    <div className="col-span-1 flex flex-col gap-1 md:gap-2 group/stat cursor-none pointer-events-auto">
                        <span className="font-mono text-[8px] md:text-[9px] text-white/40 uppercase tracking-[0.2em] group-hover/stat:text-[#38bdf8] transition-colors duration-500">{t.hero_location}</span>
                        <span className="font-mono text-[10px] md:text-xs uppercase text-[#fafafa] tracking-wider">LOS ANGELES, CA</span>
                    </div>
                    
                    <div className="col-span-1 flex flex-col gap-1 md:gap-2 group/stat cursor-none pointer-events-auto">
                        <span className="font-mono text-[8px] md:text-[9px] text-white/40 uppercase tracking-[0.2em] group-hover/stat:text-[#38bdf8] transition-colors duration-500">{t.hero_focus}</span>
                        <span className="font-mono text-[10px] md:text-xs uppercase text-[#fafafa] tracking-wider">{t.hero_digital_architecture}</span>
                    </div>

                    <div className="col-span-1 flex justify-end items-center pointer-events-auto cursor-none">
                        <motion.div 
                            whileHover={{ scale: 1.1, rotate: 180, borderColor: '#38bdf8', boxShadow: '0 0 20px rgba(56,189,248,0.2)' }}
                            transition={{ duration: 0.5 }}
                            className="w-12 h-12 md:w-20 md:h-20 rounded-full border border-white/10 flex items-center justify-center animate-[spin_10s_linear_infinite] cursor-none bg-black/20 backdrop-blur-sm shadow-[0_4px_10px_0_rgba(0,0,0,0.5)]"
                        >
                            <span className="font-mono text-[4px] md:text-[7px] uppercase tracking-[0.2em] text-center w-8 md:w-12 text-white/60">
                                {t.hero_intentional_design}
                            </span>
                        </motion.div>
                    </div>
                </motion.div>
            </div>

        </section>
    );
}